import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { UpdateOneChannalData } from '../../Redux/Actions/ChannalActions';
import { Notify } from '../../Utility/Notify';
import { useTheme } from '@mui/material/styles';
import SyncAltIcon from '@mui/icons-material/SyncAlt';

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const IconEdit = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const IconTrash = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    <line x1="10" y1="11" x2="10" y2="17"/>
    <line x1="14" y1="11" x2="14" y2="17"/>
  </svg>
);

const IconWarning = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const IconClose = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

/* ─── CSS (injected once) ────────────────────────────────────────────────── */
const CSS = `
  .aa-btn {
    display: inline-flex; align-items: center; justify-content: center;
    width: 32px; height: 32px; border-radius: 8px;
    border: 1px solid transparent; cursor: pointer;
    background: transparent;
    transition: background 0.18s, border-color 0.18s, color 0.18s, transform 0.15s;
    color: #7B829A; text-decoration: none;
  }
  .aa-btn:hover { transform: translateY(-1px); }
  .aa-btn.edit  { border-color: #E8EAF0; background: #F7F8FC; color: #1A3ECF; }
  .aa-btn.edit:hover { background: #EEF1FF; border-color: #1A3ECF; }
  .aa-btn.del   { border-color: #FFE4E4; background: #FFF5F5; color: #E53935; }
  .aa-btn.del:hover { background: #FFE4E4; border-color: #E53935; }

  .aa-overlay {
    position: fixed; inset: 0; z-index: 999999;
    display: flex; align-items: center; justify-content: center;
    background: rgba(0,0,0,0.75);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    animation: aa-fadein 0.22s ease-out both;
  }
  @keyframes aa-fadein { from { opacity: 0; } to { opacity: 1; } }

  .aa-card {
    width: 100%; max-width: 500px; margin: 0 20px;
    border-radius: 28px; padding: 40px 32px 36px;
    background: #ffffff;
    box-shadow: 0 40px 100px rgba(0,0,0,0.35);
    position: relative;
    animation: aa-slidein 0.28s cubic-bezier(0.34,1.2,0.64,1) both;
  }
  .aa-card.dark-m { background: linear-gradient(135deg,#1a1f2e 0%,#141829 100%); color: #E4E8F5; }
  @keyframes aa-slidein {
    from { opacity: 0; transform: translateY(28px) scale(0.95); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .aa-close {
    position: absolute; top: 18px; right: 18px;
    width: 36px; height: 36px; border-radius: 12px;
    border: 1px solid #E8EAF0; background: #F7F8FC;
    cursor: pointer; display: flex; align-items: center; justify-content: center;
    color: #7B829A; transition: all 0.2s;
  }
  .aa-close:hover { background: #FFE4E4; border-color: #E53935; color: #E53935; transform: scale(1.05); }
  .dark-m .aa-close { background: #252D4A; border-color: #3A4570; color: #9099c0; }
  .dark-m .aa-close:hover { background: #4a2525; border-color: #E53935; color: #E53935; }

  .aa-icon {
    width: 84px; height: 84px; border-radius: 50%;
    background: linear-gradient(135deg,#FFF3F3,#FFE4E4);
    border: 2px solid #FFBDBD;
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 22px; color: #E53935;
    box-shadow: 0 8px 24px rgba(229,57,53,0.18);
  }
  .dark-m .aa-icon { background: linear-gradient(135deg,#2a1a1a,#3a2525); border-color: #5a3535; }

  .aa-title {
    font-family: 'DM Sans', sans-serif;
    font-size: 22px; font-weight: 700;
    color: #1C2340; text-align: center;
    margin: 0 0 10px; letter-spacing: -0.3px;
  }
  .dark-m .aa-title { color: #E4E8F5; }

  .aa-sub {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px; color: #7B829A;
    text-align: center; margin: 0 0 28px; line-height: 1.65;
  }

  .aa-badge {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 7px 16px; border-radius: 40px;
    background: #F0F4FF; border: 1px solid #D6E0FF;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px; font-weight: 600; color: #1A3ECF;
    margin-bottom: 28px;
  }
  .dark-m .aa-badge { background: #252D4A; border-color: #3A4570; color: #7DA4FF; }

  .aa-actions { display: flex; gap: 12px; }

  .aa-cancel {
    flex: 1; padding: 14px 0; border-radius: 14px;
    border: 1.5px solid #E8EAF0; background: #F7F8FC;
    font-family: 'DM Sans', sans-serif; font-size: 15px; font-weight: 600;
    color: #1C2340; cursor: pointer; transition: all 0.2s;
  }
  .aa-cancel:hover { background: #EBEDF5; border-color: #C8CADC; transform: translateY(-1px); }
  .dark-m .aa-cancel { background: #252D4A; border-color: #3A4570; color: #E4E8F5; }
  .dark-m .aa-cancel:hover { background: #2E3860; }

  .aa-confirm {
    flex: 1; padding: 14px 0; border-radius: 14px; border: none;
    background: linear-gradient(135deg,#E53935,#C62828);
    font-family: 'DM Sans', sans-serif; font-size: 15px; font-weight: 700;
    color: #fff; cursor: pointer; transition: all 0.2s;
    box-shadow: 0 6px 20px rgba(229,57,53,0.3);
    display: flex; align-items: center; justify-content: center; gap: 8px;
  }
  .aa-confirm:hover { background: linear-gradient(135deg,#EF5350,#D32F2F); box-shadow: 0 8px 28px rgba(229,57,53,0.4); transform: translateY(-2px); }
  .aa-confirm:disabled { background: #E8EAF0; color: #B0B5C9; box-shadow: none; transform: none; cursor: not-allowed; }

  .aa-spin {
    width: 17px; height: 17px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: #fff; border-radius: 50%;
    animation: aa-spin 0.7s linear infinite;
  }
  @keyframes aa-spin { to { transform: rotate(360deg); } }

  @media (max-width: 560px) {
    .aa-card { padding: 30px 20px 26px; border-radius: 20px; }
    .aa-title { font-size: 19px; }
    .aa-icon  { width: 68px; height: 68px; }
  }
`;

let injected = false;
const injectCSS = () => {
  if (injected) return;
  injected = true;
  const el = document.createElement('style');
  el.innerHTML = CSS;
  document.head.appendChild(el);
};

/* ─── Delete Modal via Portal ────────────────────────────────────────────── */
const DeleteModal = ({ isDark, channelName, onClose, onConfirm, loading }) =>
  createPortal(
    <div
      className="aa-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`aa-card ${isDark ? 'dark-m' : ''}`}>

        <button className="aa-close" onClick={onClose}><IconClose /></button>

        <div className="aa-icon"><IconWarning /></div>

        <p className="aa-title">Delete Channel?</p>
        <p className="aa-sub">
          This action will deactivate the channel.<br />
          You can restore it later from the admin panel.
        </p>

        {channelName && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <span className="aa-badge">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <path d="M3 9h18M9 21V9"/>
              </svg>
              {channelName}
            </span>
          </div>
        )}

        <div className="aa-actions">
          <button className="aa-cancel" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="aa-confirm" onClick={onConfirm} disabled={loading}>
            {loading
              ? <><div className="aa-spin" />Deleting…</>
              : <><IconTrash />Delete Channel</>}
          </button>
        </div>

      </div>
    </div>,
    document.body
  );

/* ─── Main Component ─────────────────────────────────────────────────────── */
export const ChannelActions = ({ channal, allchannals }) => {
  injectCSS();

  const theme    = useTheme();
  const isDark   = theme.palette.mode === 'dark';
  const dispatch = useDispatch();

  const [show,    setShow]    = useState(false);
  const [loading, setLoading] = useState(false);

  const deleteChannel = async () => {
    setLoading(true);
    await dispatch(UpdateOneChannalData(channal._id, { active: false }));
    setLoading(false);
    setShow(false);
    Notify('success', 'Channel deleted successfully', 'success');
    allchannals();
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Link to={`/channal/updatechannal/${channal?._id}`} className="aa-btn edit" title="Edit channel">
          <IconEdit />
        </Link>
        <button className="aa-btn del" title="Delete channel" onClick={() => setShow(true)}>
          <IconTrash />
        </button>
      </div>

      {show && (
        <DeleteModal
          isDark={isDark}
          channelName={channal?.name}
          onClose={() => setShow(false)}
          onConfirm={deleteChannel}
          loading={loading}
        />
      )}
    </>
  );
};